import React, { useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  DialogOverlay,
  DialogOverlayActions,
  Paragraph,
  Separator,
  type UseOverlayReturnOptions,
} from '@fs/zion-ui'
import { Trans, useTranslation } from 'react-i18next'
import { NoticeLoading } from '@fs/zion-icon'

type PrivacyNoticeOverlayProps = {
  overlay: UseOverlayReturnOptions
  isPrivate: boolean
  isChangingSetting: boolean
  onSave: (agreementChecked: boolean) => void
  onCancel: () => void
  isSubmitting: boolean
  isCurrentlyPrivate: boolean
}

export default function PrivacyNoticeOverlay({
  overlay,
  isPrivate,
  isChangingSetting,
  onSave,
  onCancel,
  isSubmitting,
  isCurrentlyPrivate,
}: PrivacyNoticeOverlayProps): JSX.Element {
  const [t] = useTranslation()

  // Initialize checkbox as checked if tree is already private
  const [isAgreementChecked, setIsAgreementChecked] = useState(isCurrentlyPrivate)

  // Sync checkbox state when isCurrentlyPrivate changes
  useEffect(() => {
    setIsAgreementChecked(isCurrentlyPrivate)
  }, [isCurrentlyPrivate])

  // - For "Make Private": disabled unless isChangingSetting AND checkbox is checked
  // - For "Make Public": disabled unless isChangingSetting (no checkbox required)
  const isSaveDisabled = isSubmitting || !isChangingSetting || (isPrivate && !isAgreementChecked)

  const footer = (
    <DialogOverlayActions>
      <Button data-testid="privacy-notice-cancel" onClick={onCancel}>
        {t('group-management.privacy-setting.cancel')}
      </Button>
      <Button
        emphasis="high"
        data-testid="privacy-notice-save"
        onClick={() => onSave(isAgreementChecked)}
        disabled={isSaveDisabled}
        Icon={isSubmitting ? NoticeLoading : undefined}
      >
        {isPrivate
          ? t('group-management.privacy-setting.notice.private-action')
          : t('group-management.privacy-setting.notice.public-action')}
      </Button>
    </DialogOverlayActions>
  )

  return (
    <DialogOverlay
      closeOnClickAway={false}
      title={
        isPrivate
          ? t('group-management.privacy-setting.notice.private-title')
          : t('group-management.privacy-setting.notice.public-title')
      }
      footer={footer}
      {...overlay}
    >
      {isPrivate ? (
        <>
          <Paragraph size="sm">{t('group-management.privacy-setting.notice.private-description')}</Paragraph>
          <Separator size="xs" />
        </>
      ) : (
        <>
          <Paragraph size="sm" as="strong">
            {t('group-management.privacy-setting.why-public.description')}
          </Paragraph>
          <Separator size="xs" />
          <ul>
            <li>
              <Paragraph size="sm">
                {t('group-management.privacy-setting.collaborate-with-others.description')}
              </Paragraph>
            </li>
            <li>
              <Paragraph size="sm">{t('group-management.privacy-setting.help-discover.description')}</Paragraph>
            </li>
          </ul>
          <Separator size="xs" />
        </>
      )}

      <Paragraph size="sm" as="strong">
        {t('group-management.privacy-setting.notice.who-sees.title')}
      </Paragraph>
      <Paragraph size="sm">
        {isPrivate
          ? t('group-management.privacy-setting.notice.who-sees.description.private')
          : t('group-management.privacy-setting.notice.who-sees.description.public')}
      </Paragraph>

      {isPrivate && (
        <>
          <Paragraph size="sm" as="strong">
            {t('group-management.privacy-setting.notice.how-will.title')}
          </Paragraph>
          <Paragraph size="sm">{t('group-management.privacy-setting.notice.how-will.description')}</Paragraph>
          <Paragraph size="sm" as="strong">
            {t('group-management.privacy-setting.notice.how-long.title')}
          </Paragraph>
          <Paragraph size="sm">{t('group-management.privacy-setting.notice.how-long.description')}</Paragraph>
        </>
      )}

      <Paragraph size="sm">
        <Trans i18nKey="group-management.privacy-setting.notice.note" />
      </Paragraph>
      {isPrivate && (
        <Checkbox
          data-testid="privacy-notice-agreement-checkbox"
          label={t('group-management.privacy-setting.notice.agreement')}
          name="privacy-notice-confirmation"
          checked={isAgreementChecked}
          onChange={() => setIsAgreementChecked(!isAgreementChecked)}
          disabled={isCurrentlyPrivate}
        />
      )}
    </DialogOverlay>
  )
}
